import React, {
  useEffect, useId, useMemo, useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getV2Flow } from '../../../api/studioV2.api';
import { COMMON_OUTPUT_PATH_HINTS, collectOutputPathHints } from '../studioV2BindingHints';
import {
  PANEL_KINDS, SIMPLE_LAYOUTS, ZONE_LABELS, addPanel, bindingCount, chatFlowOf, chatMemoryOf,
  hiddenPanelsForLayout, isGridApp, layoutInfo, layoutThumbnail, movePanel, panelKind, panelLabel, panelPhase,
  panelsInZone, removePanel, setChatFlow, setChatMemory, setLayout, setPanelConfig, setPanelFlow,
  setPanelResultField, setPanelSkill, zoneCapacity,
} from './appBuilderModel';
import { listExtensionSkills } from '../../../api/extensions.api';
import { loadWorkspacePluginPanels } from '../../Dashboard/pluginPanels';
import AppGridArranger from './AppGridArranger';
import { LayoutSketch, PanelIcon } from './panelVisuals';

function SectionTitle({ title, hint }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      {hint && <Typography variant="body2" color="text.secondary">{hint}</Typography>}
    </Box>
  );
}

function FlowSelect({
  label, value, flows, onChange, disabled, emptyLabel = 'Not connected',
}) {
  const known = flows.some((f) => String(f._id) === String(value));
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>{label}</InputLabel>
        <Select label={label} value={value && known ? String(value) : ''} onChange={(e) => onChange(e.target.value)} displayEmpty>
          <MenuItem value=""><em>{value && !known ? 'A flow outside this workspace' : emptyLabel}</em></MenuItem>
          {flows.map((f) => (
            <MenuItem key={f._id} value={String(f._id)}>{f.name || f.definition?.name || 'Untitled flow'}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {value && known && (
        <Tooltip title="Open this flow in a new tab">
          <IconButton
            size="small"
            component="a"
            href={`/studio/flows/${value}`}
            target="_blank"
            rel="noopener"
            aria-label="Open flow"
            data-testid="app-open-flow"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

function SkillSelect({
  value, skills, disabled, onChange,
}) {
  const known = skills.some((s) => s.id === value);
  const labelId = useId();
  return (
    <FormControl size="small" fullWidth disabled={disabled}>
      <InputLabel id={labelId}>Skill to talk to</InputLabel>
      <Select
        labelId={labelId}
        label="Skill to talk to"
        value={value && known ? value : ''}
        onChange={(e) => onChange(skills.find((s) => s.id === e.target.value) || null)}
        displayEmpty
        inputProps={{ 'data-testid': 'app-skill-select' }}
      >
        <MenuItem value=""><em>{value && !known ? 'A skill that is no longer available' : 'Choose a skill'}</em></MenuItem>
        {skills.map((s) => (
          <MenuItem key={s.id} value={s.id}>{s.name || s.id}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function PanelCard({
  def, panel, flows, flowHints, skills, pluginPanels, canEdit, onChange, onOpenAdvanced, isFirst, isLast, hidden,
}) {
  const [open, setOpen] = useState(false);
  const kind = panelKind(panel);
  const isChat = panel.type === 'chat';
  const isSkillChat = kind === 'skill_chat';
  const isPlugin = kind === 'plugin';
  const isComponent = panel.type === 'component' && !isSkillChat;
  const plugin = isPlugin ? pluginPanels.find((p) => p.ref === panel.config?.pluginPanel) : null;
  const flowRef = isChat ? chatFlowOf(def) : panel.source?.flowRef;
  const flowName = flows.find((f) => String(f._id) === String(flowRef))?.name;
  const connected = isSkillChat ? Boolean(panel.config?.skillId) : Boolean(flowRef);
  const memory = chatMemoryOf(def);
  const hints = (flowRef && flowHints[flowRef]) || COMMON_OUTPUT_PATH_HINTS;
  const mappings = bindingCount(panel);
  const phaseId = panelPhase(panel);
  const phases = def.metadata?.interviewFlow?.phases;
  const phaseName = phaseId
    ? ((Array.isArray(phases) && phases.find((p) => p?.id === phaseId)?.label) || phaseId)
    : '';

  let status = null;
  if (isChat) status = connected ? `Answers with ${flowName || 'a linked flow'}` : 'Pick the flow that answers';
  else if (isSkillChat) status = connected ? `Talks to ${panel.config?.skillName || 'a skill'}` : 'Pick the skill it talks to';
  else if (isComponent) status = connected ? `Shows results from ${flowName || 'a linked flow'}` : 'Not connected to a flow yet';
  else status = 'Recaps the conversation automatically';

  return (
    <Paper
      variant="outlined"
      data-testid={`design-panel-${panel.id}`}
      sx={{
        borderRadius: 2.5,
        overflow: 'hidden',
        opacity: hidden ? 0.6 : 1,
        transition: 'box-shadow .15s',
        '&:hover': { boxShadow: '0 4px 14px rgba(15,23,42,.06)' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 1.5 }}>
        <PanelIcon kind={kind} />
        <ButtonBase onClick={() => setOpen((v) => !v)} sx={{ flex: 1, justifyContent: 'flex-start', textAlign: 'left', borderRadius: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="body2" fontWeight={700}>{panelLabel(panel)}</Typography>
              {phaseName && (
                <Chip size="small" variant="outlined" label={`${phaseName} step`} sx={{ height: 20, fontSize: 11, textTransform: 'capitalize' }} />
              )}
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {(isChat || isComponent || isSkillChat) && (connected
                ? <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                : <LinkOffIcon sx={{ fontSize: 14, color: 'warning.main' }} />)}
              <Typography variant="caption" color="text.secondary" noWrap>
                {hidden ? 'Hidden by this layout' : status}
              </Typography>
            </Stack>
          </Box>
        </ButtonBase>
        {canEdit && (
          <Stack direction="row">
            <Tooltip title="Move up"><span><IconButton size="small" disabled={isFirst} onClick={() => onChange(movePanel(def, panel.id, 'up'))}><ArrowUpwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
            <Tooltip title="Move down"><span><IconButton size="small" disabled={isLast} onClick={() => onChange(movePanel(def, panel.id, 'down'))}><ArrowDownwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
            <Tooltip title="Remove panel">
              <IconButton
                size="small"
                aria-label={`Remove ${panelLabel(panel)}`}
                onClick={() => {
                  if (window.confirm(`Remove the ${panelLabel(panel).toLowerCase()} panel?`)) onChange(removePanel(def, panel.id));
                }}
              >
                <DeleteOutlineIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
        <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label="Panel settings">
          <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </IconButton>
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={1.5} sx={{ px: 2, pb: 2, pt: 0.5 }}>
          {isChat && (
            <>
              <FlowSelect
                label="Flow that answers the chat"
                value={flowRef}
                flows={flows}
                disabled={!canEdit}
                onChange={(v) => onChange(setChatFlow(def, v))}
              />
              <FormControlLabel
                disabled={!canEdit}
                control={<Switch size="small" checked={memory.rememberOnDevice} onChange={(e) => onChange(setChatMemory(def, { rememberOnDevice: e.target.checked }))} />}
                label={<Typography variant="body2">Remember the conversation when people come back</Typography>}
              />
              <FormControlLabel
                disabled={!canEdit}
                control={<Switch size="small" checked={memory.shareWithPanels} onChange={(e) => onChange(setChatMemory(def, { shareWithPanels: e.target.checked }))} />}
                label={<Typography variant="body2">Let the other panels use what was said in the chat</Typography>}
              />
            </>
          )}
          {(isSkillChat || isPlugin) && (
            <TextField
              size="small"
              fullWidth
              disabled={!canEdit}
              label="Panel title"
              value={panel.config?.title || ''}
              onChange={(e) => onChange(setPanelConfig(def, panel.id, { title: e.target.value.slice(0, 80) }))}
            />
          )}
          {isSkillChat && (
            <>
              <SkillSelect
                value={panel.config?.skillId || ''}
                skills={skills}
                disabled={!canEdit}
                onChange={(skill) => onChange(setPanelSkill(def, panel.id, skill))}
              />
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                disabled={!canEdit}
                label="Greeting (optional)"
                placeholder="Hi! Ask me about…"
                value={panel.config?.greeting || ''}
                onChange={(e) => onChange(setPanelConfig(def, panel.id, { greeting: e.target.value.slice(0, 500) }))}
              />
              <Typography variant="caption" color="text.secondary">
                People chat with this skill directly, no flow needed. It can use the tools the skill allows.
              </Typography>
            </>
          )}
          {isPlugin && (
            plugin
              ? (
                <Typography variant="caption" color="text.secondary">
                  {`Drawn by the ${plugin.extensionName || plugin.extensionId} plugin. ${plugin.description || ''}`}
                </Typography>
              )
              : (
                <Alert severity="warning" sx={{ py: 0 }}>
                  The plugin for this panel isn&apos;t installed or enabled in this workspace.
                </Alert>
              )
          )}
          {isComponent && (
            <>
              <FlowSelect
                label="Fill this panel from"
                value={flowRef}
                flows={flows}
                disabled={!canEdit}
                onChange={(v) => onChange(setPanelFlow(def, panel.id, v))}
              />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Which part of the flow&apos;s result to show
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75} sx={{ mb: 1 }}>
                  {hints.slice(0, 8).map((hint) => (
                    <Chip
                      key={hint}
                      size="small"
                      label={hint}
                      disabled={!canEdit}
                      color={panel.source?.outputPath === hint ? 'primary' : 'default'}
                      variant={panel.source?.outputPath === hint ? 'filled' : 'outlined'}
                      onClick={() => onChange(setPanelResultField(def, panel.id, hint))}
                    />
                  ))}
                </Stack>
                <TextField
                  size="small"
                  fullWidth
                  disabled={!canEdit}
                  label="Result field"
                  placeholder="e.g. result.candidates"
                  value={panel.source?.outputPath || ''}
                  onChange={(e) => onChange(setPanelResultField(def, panel.id, e.target.value))}
                />
              </Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  {mappings
                    ? `${mappings} field mapping${mappings === 1 ? '' : 's'} customize what each column or value shows.`
                    : 'Fields are matched automatically.'}
                </Typography>
                <Button size="small" onClick={() => onOpenAdvanced('layout')} sx={{ textTransform: 'none' }}>
                  {kind === 'form' ? 'Edit form fields' : 'Fine-tune fields'}
                </Button>
              </Stack>
            </>
          )}
          {panel.type === 'summary' && (
            <Typography variant="body2" color="text.secondary">
              This panel keeps a short recap of the conversation. There is nothing to set up.
            </Typography>
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
}

const tileSx = {
  p: 1.5,
  borderRadius: 2,
  border: 1,
  borderColor: 'divider',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  textAlign: 'left',
  gap: 1,
  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
};

function AddPanelDialog({
  def, zone, pluginPanels, onClose, onAdd,
}) {
  const [target, setTarget] = useState(zone);
  useEffect(() => setTarget(zone), [zone]);
  const layout = layoutInfo(def.layout) || SIMPLE_LAYOUTS[0];
  const capacity = target ? zoneCapacity(def, target) : { fits: false };
  const hasChat = def.panels.some((p) => p.type === 'chat');

  return (
    <Dialog open={Boolean(zone)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Add a panel</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
          <Typography variant="body2" color="text.secondary">Place it:</Typography>
          {layout.zones.map((z) => {
            const c = zoneCapacity(def, z);
            return (
              <Chip
                key={z}
                label={ZONE_LABELS[z]}
                color={target === z ? 'primary' : 'default'}
                variant={target === z ? 'filled' : 'outlined'}
                disabled={!c.fits}
                onClick={() => setTarget(z)}
              />
            );
          })}
        </Stack>
        {capacity.switchTo && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This side already has a panel, so the layout will switch to stacked panels.
          </Alert>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1.25 }}>
          {PANEL_KINDS.filter((k) => !k.pickerHidden && !(k.kind === 'chat' && hasChat)).map((k) => (
            <ButtonBase
              key={k.kind}
              disabled={!capacity.fits}
              onClick={() => onAdd(k.kind, target)}
              data-testid={`add-panel-${k.kind}`}
              sx={tileSx}
            >
              <PanelIcon kind={k.kind} size={32} />
              <Box>
                <Typography variant="body2" fontWeight={700}>{k.label}</Typography>
                <Typography variant="caption" color="text.secondary">{k.description}</Typography>
              </Box>
            </ButtonBase>
          ))}
        </Box>
        {pluginPanels.length > 0 && (
          <>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                display: 'block', mt: 2.5, mb: 1, letterSpacing: 1,
              }}
            >
              From your plugins
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1.25 }}>
              {pluginPanels.map((p) => (
                <ButtonBase
                  key={p.ref}
                  disabled={!capacity.fits}
                  onClick={() => onAdd('plugin', target, p)}
                  data-testid={`add-plugin-panel-${p.ref}`}
                  sx={tileSx}
                >
                  <PanelIcon kind="plugin" size={32} />
                  <Box>
                    <Typography variant="body2" fontWeight={700}>{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.description || `From ${p.extensionName}`}</Typography>
                  </Box>
                </ButtonBase>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Plain-language "Design" tab: layout choice and panels, grouped by where they sit. */
export default function AppDesignPanel({
  def, flows, canEdit, onChange, onOpenAdvanced,
}) {
  const [addZone, setAddZone] = useState(null);
  const [flowHints, setFlowHints] = useState({});
  const [skills, setSkills] = useState([]);
  const [pluginPanels, setPluginPanels] = useState([]);
  const layout = layoutInfo(def.layout);

  useEffect(() => {
    let cancelled = false;
    listExtensionSkills().then(({ skills: list }) => { if (!cancelled) setSkills(list); });
    loadWorkspacePluginPanels({ force: true }).then(({ panels }) => { if (!cancelled) setPluginPanels(panels); });
    return () => { cancelled = true; };
  }, []);
  const hiddenIds = new Set(hiddenPanelsForLayout(def, def.layout).map((p) => p.id));

  const flowRefs = useMemo(() => [...new Set(def.panels
    .map((p) => String(p.source?.flowRef || '').trim())
    .filter(Boolean))].sort().join(','), [def.panels]);

  useEffect(() => {
    if (!flowRefs) return undefined;
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(flowRefs.split(',').map(async (ref) => {
        const { flow } = await getV2Flow(ref);
        next[ref] = flow?.definition ? collectOutputPathHints(flow.definition) : COMMON_OUTPUT_PATH_HINTS;
      }));
      if (!cancelled) setFlowHints(next);
    })();
    return () => { cancelled = true; };
  }, [flowRefs]);

  const zones = layout ? layout.zones : ['left', 'center', 'right', 'top', 'bottom'];
  const accent = '#2563eb';

  return (
    <Stack spacing={3.5}>
      <Box>
        <SectionTitle title="Layout" hint="How the screen is arranged." />
        {!layout && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {isGridApp(def)
              ? 'This app uses a free-form grid. Drag and resize panels under Arrange, or pick a fixed layout below.'
              : 'This app uses a custom layout. Pick one below to simplify it, or keep editing it in Advanced.'}
          </Alert>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
          {SIMPLE_LAYOUTS.map((l) => {
            const selected = def.layout === l.id;
            const hides = hiddenPanelsForLayout(def, l.id);
            return (
              <ButtonBase
                key={l.id}
                disabled={!canEdit}
                onClick={() => onChange(setLayout(def, l.id))}
                data-testid={`layout-${l.id}`}
                aria-pressed={selected}
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  border: 2,
                  borderColor: selected ? 'primary.main' : 'divider',
                  bgcolor: selected ? 'rgba(37,99,235,.04)' : 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  textAlign: 'left',
                  gap: 1,
                }}
              >
                <LayoutSketch columns={layoutThumbnail({ ...def, layout: l.id })} accent={accent} height={44} />
                <Box>
                  <Typography variant="body2" fontWeight={700}>{l.label}</Typography>
                  <Typography variant="caption" color={hides.length && !selected ? 'warning.main' : 'text.secondary'}>
                    {hides.length && !selected ? `Would hide: ${hides.map(panelLabel).join(', ')}` : l.description}
                  </Typography>
                </Box>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {isGridApp(def) && (
        <Box>
          <SectionTitle title="Arrange" hint="Drag panels to move them; drag a corner to resize." />
          <AppGridArranger def={def} canEdit={canEdit} onChange={onChange} />
        </Box>
      )}

      <Box>
        <SectionTitle title="Panels" hint="What people see. Open a panel to connect it to a flow." />
        <Stack spacing={2.5}>
          {zones.map((zone) => {
            const panels = panelsInZone(def, zone);
            const capacity = zoneCapacity(def, zone);
            if (!panels.length && !capacity.fits) return null;
            return (
              <Box key={zone}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                    {ZONE_LABELS[zone]}
                  </Typography>
                  {canEdit && capacity.fits && (
                    <Button size="small" startIcon={<AddIcon />} onClick={() => setAddZone(zone)} sx={{ textTransform: 'none' }}>
                      Add panel
                    </Button>
                  )}
                </Stack>
                <Stack spacing={1}>
                  {panels.map((panel, i) => (
                    <PanelCard
                      key={panel.id}
                      def={def}
                      panel={panel}
                      flows={flows}
                      flowHints={flowHints}
                      skills={skills}
                      pluginPanels={pluginPanels}
                      canEdit={canEdit}
                      onChange={onChange}
                      onOpenAdvanced={onOpenAdvanced}
                      isFirst={i === 0}
                      isLast={i === panels.length - 1}
                      hidden={hiddenIds.has(panel.id)}
                    />
                  ))}
                  {!panels.length && (
                    <ButtonBase
                      disabled={!canEdit}
                      onClick={() => setAddZone(zone)}
                      sx={{
                        p: 2, borderRadius: 2.5, border: 1, borderStyle: 'dashed', borderColor: 'divider', color: 'text.secondary',
                      }}
                    >
                      <AddIcon fontSize="small" sx={{ mr: 0.75 }} />
                      <Typography variant="body2">Empty. Add a panel here</Typography>
                    </ButtonBase>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {addZone && (
        <AddPanelDialog
          def={def}
          zone={addZone}
          pluginPanels={pluginPanels}
          onClose={() => setAddZone(null)}
          onAdd={(kind, zone, plugin) => {
            onChange(addPanel(def, kind, zone, plugin));
            setAddZone(null);
          }}
        />
      )}
    </Stack>
  );
}
